import { useState, useEffect, useCallback } from 'react';
import ProjectBar from './components/ProjectBar';
import Board from './components/Board';
import TaskForm from './components/TaskForm';
import {
  Project, Task, TaskStatus, TaskSource, Stats,
  getProjects, createProject, deleteProject,
  getTasks, createTask, moveTask, reorderTasks, deleteTask, getStats,
} from './lib/api';

export default function App() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draggedTaskId, setDraggedTaskId] = useState<number | null>(null);

  const refreshStats = useCallback(async () => {
    try {
      setStats(await getStats());
    } catch {
      /* ignore */
    }
  }, []);

  const refreshTasks = useCallback(async (projectId: number) => {
    try {
      setTasks(await getTasks(projectId));
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const data = await getProjects();
        setProjects(data);
        if (data.length > 0) setSelectedId(data[0].id);
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setLoading(false);
      }
    })();
    refreshStats();
  }, [refreshStats]);

  useEffect(() => {
    if (selectedId != null) refreshTasks(selectedId);
  }, [selectedId, refreshTasks]);

  const handleCreateProject = async (name: string) => {
    const p = await createProject(name);
    setProjects((prev) => [p, ...prev]);
    setSelectedId(p.id);
    refreshStats();
  };

  const handleDeleteProject = async (id: number) => {
    await deleteProject(id);
    setProjects((prev) => prev.filter((p) => p.id !== id));
    if (selectedId === id) {
      setSelectedId(null);
      setTasks([]);
    }
    refreshStats();
  };

  const handleCreateTask = async (data: { title: string; session_link?: string; source?: TaskSource }) => {
    if (selectedId == null) return;
    const t = await createTask({ project_id: selectedId, ...data });
    setTasks((prev) => [...prev, t]);
    refreshStats();
  };

  const handleDeleteTask = async (taskId: number) => {
    await deleteTask(taskId);
    setTasks((prev) => prev.filter((t) => t.id !== taskId));
    refreshStats();
  };

  const handleDragStartTask = (id: number) => setDraggedTaskId(id);

  const handleDropTask = async (status: TaskStatus, targetIndex: number) => {
    if (draggedTaskId == null || selectedId == null) return;
    const dragged = tasks.find((t) => t.id === draggedTaskId);
    if (!dragged) {
      setDraggedTaskId(null);
      return;
    }

    const sourceStatus = dragged.status;
    const others = tasks.filter((t) => t.id !== draggedTaskId);

    const targetColumn = others
      .filter((t) => t.status === status)
      .sort((a, b) => a.position - b.position);
    const idx = Math.max(0, Math.min(targetIndex, targetColumn.length));
    targetColumn.splice(idx, 0, { ...dragged, status });

    setTasks([...others, { ...dragged, status }]);
    setDraggedTaskId(null);

    try {
      if (sourceStatus !== status) {
        await moveTask(draggedTaskId, status);
      }
      await reorderTasks(selectedId, status, targetColumn.map((t) => t.id));
      if (sourceStatus !== status) {
        const sourceIds = others
          .filter((t) => t.status === sourceStatus)
          .sort((a, b) => a.position - b.position)
          .map((t) => t.id);
        await reorderTasks(selectedId, sourceStatus, sourceIds);
      }
      await refreshTasks(selectedId);
      refreshStats();
    } catch (e) {
      setError((e as Error).message);
      await refreshTasks(selectedId);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="bg-kanban-navy text-white px-4 py-3 shadow-sm">
        <h1 className="text-lg font-semibold">Kanban Task Tracker</h1>
      </header>

      <div className="px-4 py-3 bg-white border-b border-kanban-border">
        <ProjectBar
          projects={projects}
          selectedId={selectedId}
          doneCount={stats?.done ?? 0}
          onSelect={setSelectedId}
          onCreate={handleCreateProject}
          onDelete={handleDeleteProject}
        />
      </div>

      {error && (
        <div className="mx-4 mt-3 px-4 py-2 rounded-lg bg-red-50 text-red-700 text-sm">
          {error}
          <button className="ml-3 underline" onClick={() => setError(null)}>Fermer</button>
        </div>
      )}

      <main className="flex-1 p-4 flex flex-col gap-4">
        <TaskForm
          projects={projects}
          selectedProjectId={selectedId}
          onProjectSelect={setSelectedId}
          onCreate={handleCreateTask}
        />

        {loading ? (
          <div className="flex-1 flex items-center justify-center text-text-muted">Chargement…</div>
        ) : selectedId == null ? (
          <div className="flex-1 flex items-center justify-center text-text-muted">
            Créez un projet pour commencer.
          </div>
        ) : (
          <Board
            tasks={tasks}
            draggedTaskId={draggedTaskId}
            onDropTask={handleDropTask}
            onDeleteTask={handleDeleteTask}
            onDragStartTask={handleDragStartTask}
          />
        )}
      </main>
    </div>
  );
}
